import * as React from 'react';
import { useContext, useState } from 'react';
import { useParams } from 'react-router';
import { FileContext } from '../../components/Files/FileContext';
import { authAxios } from '../../services/AxiosService';
import { notifyError, notifySuccess } from '../../services/ToastService';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
} from 'reactstrap';
import { ConfirmModal } from '../../components/ConfirmModal/ConfirmModal';
import {
  FILE_SUB_TYPE_COMMUNITY_IMPACT,
  FILE_SUB_TYPE_GLADUE_REPORT,
  FILE_SUB_TYPE_LEGAL_SYSTEM_NAVIGATOR,
  FILE_SUB_TYPE_WCC_CONNECTIVE,
  FILE_TYPE_AUNTIES_HOUSE,
  FILE_TYPE_BIRTH_WORKER,
  FILE_TYPE_CULTURAL_CONNECTIONS,
  FILE_TYPE_FAMILY_PRESERVATION,
  FILE_TYPE_JORDANS_PRINCIPLE,
  FILE_TYPE_JUSTICE,
  FILE_TYPE_POST_MAJORITY_SUPPORT_SERVICES,
  FILE_TYPE_WELLNESS_CENTRE,
  STAGE_ACTIVE,
  STAGE_DRAFT,
  STAGE_IN_PROGRESS,
  STAGE_INTAKE_IN_PROGRESS,
  STAGE_NEEDS_MORE_INFORMATION,
  STAGE_REJECTED,
  STAGE_REQUESTED,
  STAGE_WAITLIST,
} from '../../contexts/DomainContext';
import { formatDateFromISONumber } from '../../formatters/DateTimeFormatter';

export const IntakeFileRequestActions = () => {
  const params = useParams<{ fileId: string }>();
  const fileContext = useContext(FileContext);
  const setFile = fileContext.setFile;
  const setStageId = fileContext.setStageId;
  const file = fileContext.file;
  const reason = fileContext.reason;
  const setReason = fileContext.setReason;
  const needsMoreInformationNote = fileContext.needsMoreInformationNote;
  const setNeedsMoreInformationNote = fileContext.setNeedsMoreInformationNote;

  const [trySubmit, setTrySubmit] = useState(false);

  const [acceptModal, setAcceptModal] = useState(false);
  const [waitlistModal, setWaitlistModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [needsMoreInformationModal, setNeedsMoreInformationModal] = useState(false);
  const [returnToIntakeInProgressModal, setReturnToIntakeInProgressModal] = useState(false);

  const acceptRequest = () => {
    const getAcceptedStage = () => {
      if (fileContext.fileTypeId === FILE_TYPE_JORDANS_PRINCIPLE) {
        return STAGE_DRAFT;
      } else if (fileContext.fileTypeId === FILE_TYPE_BIRTH_WORKER) {
        return STAGE_ACTIVE;
      } else if (fileContext.fileTypeId === FILE_TYPE_FAMILY_PRESERVATION) {
        return STAGE_ACTIVE;
      } else if (fileContext.fileTypeId === FILE_TYPE_POST_MAJORITY_SUPPORT_SERVICES) {
        return STAGE_ACTIVE;
      } else if (fileContext.fileTypeId === FILE_TYPE_CULTURAL_CONNECTIONS) {
        return STAGE_ACTIVE;
      } else if (fileContext.fileTypeId === FILE_TYPE_AUNTIES_HOUSE) {
        return STAGE_ACTIVE;
      } else if (
        fileContext.fileTypeId === FILE_TYPE_JUSTICE &&
        fileContext.fileSubTypeId === FILE_SUB_TYPE_LEGAL_SYSTEM_NAVIGATOR
      ) {
        return STAGE_ACTIVE;
      } else if (
        fileContext.fileTypeId === FILE_TYPE_JUSTICE &&
        fileContext.fileSubTypeId === FILE_SUB_TYPE_WCC_CONNECTIVE
      ) {
        return STAGE_ACTIVE;
      } else if (
        fileContext.fileTypeId === FILE_TYPE_JUSTICE &&
        fileContext.fileSubTypeId === FILE_SUB_TYPE_COMMUNITY_IMPACT
      ) {
        return STAGE_IN_PROGRESS;
      } else if (
        fileContext.fileTypeId === FILE_TYPE_JUSTICE &&
        fileContext.fileSubTypeId === FILE_SUB_TYPE_GLADUE_REPORT
      ) {
        return STAGE_REQUESTED;
      } else if (fileContext.fileTypeId === FILE_TYPE_WELLNESS_CENTRE) {
        return STAGE_ACTIVE;
      } else {
        return '';
      }
    };
    const acceptedStage = getAcceptedStage();
    if (acceptedStage) {
      const data = {
        fileId: params.fileId,
        stageId: acceptedStage,
      };
      authAxios.post('/api/files/admin/accept-file-request', data).then((response) => {
        if (response.data.status === 'OK') {
          notifySuccess('Request Accepted.');
          setFile((f) => ({ ...f, stageId: acceptedStage, acceptedDate: response.data.acceptedDate }));
          setStageId(acceptedStage);
        } else {
          notifyError(response.data.errorMessage);
        }
      });
    } else {
      notifyError('Invalid Intake file');
    }
  };

  const rejectRequest = (reason?: string) => {
    if (reason) {
      const data = {
        fileId: params.fileId,
        stageId: STAGE_REJECTED,
        reason: reason,
      };
      authAxios.post('/api/files/admin/reject-file-request', data).then((response) => {
        if (response.data.status === 'OK') {
          notifySuccess('Request Declined.');
          setFile((f) => ({
            ...f,
            stageId: STAGE_REJECTED,
            rejectedDate: response.data.rejectedDate,
            reason,
          }));
          setStageId(STAGE_REJECTED);
        } else {
          notifyError(response.data.errorMessage);
        }
      });
    } else {
      setTrySubmit(true);
    }
  };

  const waitlistRequest = () => {
    const data = {
      fileId: params.fileId,
      stageId: STAGE_WAITLIST,
    };
    authAxios.post('/api/files/admin/waitlist-file-request', data).then((response) => {
      if (response.data.status === 'OK') {
        notifySuccess('Request Waitlisted.');
        setFile((f) => ({
          ...f,
          stageId: STAGE_WAITLIST,
          acceptedDate: response.data.acceptedDate,
        }));
        setStageId(STAGE_WAITLIST);
      } else {
        notifyError(response.data.errorMessage);
      }
    });
  };

  const needsMoreInformationRequest = () => {
    const data = {
      fileId: params.fileId,
      stageId: STAGE_NEEDS_MORE_INFORMATION,
      needsMoreInformationNote: needsMoreInformationNote,
    };
    authAxios.post('/api/files/admin/needs-more-information-file-request', data).then((response) => {
      if (response.data.status === 'OK') {
        notifySuccess('Request Updated.');
        setFile((f) => ({
          ...f,
          stageId: STAGE_NEEDS_MORE_INFORMATION,
          needsMoreInformationNote,
        }));
        setStageId(STAGE_NEEDS_MORE_INFORMATION);
      } else {
        notifyError(response.data.errorMessage);
      }
    });
  };

  const intakeInProgressRequest = () => {
    const data = {
      fileId: params.fileId,
      stageId: STAGE_INTAKE_IN_PROGRESS,
    };
    authAxios.post('/api/files/admin/intake-in-progress-file-request', data).then((response) => {
      if (response.data.status === 'OK') {
        notifySuccess('Request Updated.');
        setFile((f) => ({
          ...f,
          stageId: STAGE_INTAKE_IN_PROGRESS,
        }));
        setStageId(STAGE_INTAKE_IN_PROGRESS);
      } else {
        notifyError(response.data.errorMessage);
      }
    });
  };

  return (
    <>
      <Card className={' mb-3'}>
        <CardHeader>
          {file.stageId === STAGE_INTAKE_IN_PROGRESS
            ? 'Accept or Decline File Request'
            : file.stageId === STAGE_REJECTED
            ? 'File Request Declined'
            : file.stageId === STAGE_NEEDS_MORE_INFORMATION
            ? 'File Request Needs More Information'
            : 'File Request Accepted'}
        </CardHeader>
        {file.stageId !== STAGE_REJECTED &&
          file.stageId !== STAGE_INTAKE_IN_PROGRESS &&
          file.stageId !== STAGE_NEEDS_MORE_INFORMATION && (
            <CardBody>
              <Row>
                <Col>File Request Accepted on {formatDateFromISONumber(file.acceptedDate)}</Col>
              </Row>
            </CardBody>
          )}
        {file.stageId === STAGE_REJECTED && (
          <CardBody>
            <Row>
              <Col>
                File Declined on {formatDateFromISONumber(file.rejectedDate)} for reason: {file.reason}
              </Col>
            </Row>
          </CardBody>
        )}
        {file.stageId === STAGE_INTAKE_IN_PROGRESS && (
          <ListGroup flush={true}>
            <ListGroupItem>
              <Button
                color='primary'
                className='ml-auto'
                disabled={fileContext.stageId !== STAGE_INTAKE_IN_PROGRESS}
                onClick={() => setAcceptModal(true)}
              >
                Accept Request
              </Button>
            </ListGroupItem>
            {file.fileTypeId === FILE_TYPE_WELLNESS_CENTRE && (
              <ListGroupItem>
                <Button
                  color='primary'
                  className='ml-auto'
                  disabled={fileContext.stageId !== STAGE_INTAKE_IN_PROGRESS}
                  onClick={() => setWaitlistModal(true)}
                >
                  Accept and Waitlist Request
                </Button>
              </ListGroupItem>
            )}
            <ListGroupItem>
              <FormGroup>
                <Label for={'reason'}>If declining, please provide a reason</Label>
                <Input
                  value={file.reason}
                  type={'text'}
                  disabled={fileContext.stageId !== STAGE_INTAKE_IN_PROGRESS}
                  name={'Reason'}
                  autoComplete={'off'}
                  onChange={(e: any) => setReason(e.target.value)}
                  invalid={trySubmit && !reason}
                />
                <FormFeedback>This field is mandatory</FormFeedback>
              </FormGroup>
              <Button
                color='danger'
                className='ml-auto'
                disabled={fileContext.stageId !== STAGE_INTAKE_IN_PROGRESS}
                onClick={() => {
                  setTrySubmit(true);
                  if (reason) {
                    setRejectModal(true);
                  }
                }}
              >
                Decline Request
              </Button>
            </ListGroupItem>
            {file.fileTypeId === FILE_TYPE_WELLNESS_CENTRE && (
              <ListGroupItem>
                <FormGroup>
                  <Label for={'NeedsMoreInformationNote'}>Notes</Label>
                  <Input
                    value={needsMoreInformationNote}
                    type={'text'}
                    disabled={fileContext.stageId !== STAGE_INTAKE_IN_PROGRESS}
                    name={'NeedsMoreInformationNote'}
                    autoComplete={'off'}
                    onChange={(e: any) => setNeedsMoreInformationNote(e.target.value)}
                  />
                </FormGroup>
                <Button
                  color='warning'
                  className='ml-auto'
                  disabled={fileContext.stageId !== STAGE_INTAKE_IN_PROGRESS}
                  onClick={() => {
                    setNeedsMoreInformationModal(true);
                  }}
                >
                  More Information Needed
                </Button>
              </ListGroupItem>
            )}
          </ListGroup>
        )}
        {file.stageId === STAGE_NEEDS_MORE_INFORMATION && (
          <ListGroup flush={true}>
            <ListGroupItem>
              <FormGroup>
                <Label for={'NeedsMoreInformationNote'}>Notes</Label>
                <Input
                  value={needsMoreInformationNote}
                  type={'text'}
                  disabled={fileContext.stageId !== STAGE_INTAKE_IN_PROGRESS}
                  name={'NeedsMoreInformationNote'}
                  autoComplete={'off'}
                  onChange={(e: any) => setNeedsMoreInformationNote(e.target.value)}
                />
              </FormGroup>
              <Button
                color='warning'
                className='ml-auto'
                disabled={fileContext.stageId !== STAGE_NEEDS_MORE_INFORMATION}
                onClick={() => {
                  setReturnToIntakeInProgressModal(true);
                }}
              >
                Return to Intake-In-Progress
              </Button>
            </ListGroupItem>
          </ListGroup>
        )}
      </Card>
      <ConfirmModal
        isOpen={acceptModal}
        title={'Accept'}
        onConfirm={() => {
          setAcceptModal(false);
          acceptRequest();
        }}
        onCancel={() => setAcceptModal(false)}
        confirmButton={'Accept'}
      >
        <div>Are you sure you want to accept the request?</div>
      </ConfirmModal>
      <ConfirmModal
        isOpen={rejectModal}
        title={'Decline'}
        onConfirm={() => {
          setRejectModal(false);
          rejectRequest(reason);
        }}
        onCancel={() => setRejectModal(false)}
        confirmButton={'Decline'}
      >
        <div>Are you sure you want to decline the request?</div>
      </ConfirmModal>
      <ConfirmModal
        isOpen={waitlistModal}
        title={'Waitlist'}
        onConfirm={() => {
          setWaitlistModal(false);
          waitlistRequest();
        }}
        onCancel={() => setWaitlistModal(false)}
        confirmButton={'Accept and Waitlist'}
      >
        <div>Are you sure you want to accept and waitlist this request?</div>
      </ConfirmModal>
      <ConfirmModal
        isOpen={needsMoreInformationModal}
        title={'Send Request Back For More Information'}
        onConfirm={() => {
          setNeedsMoreInformationModal(false);
          needsMoreInformationRequest();
        }}
        onCancel={() => setNeedsMoreInformationModal(false)}
        confirmButton={'Yes'}
      >
        <div>Are you sure you want to send the request back for more information?</div>
      </ConfirmModal>
      <ConfirmModal
        isOpen={returnToIntakeInProgressModal}
        title={'Return File Request Back to Intake-In-Progress'}
        onConfirm={() => {
          setReturnToIntakeInProgressModal(false);
          intakeInProgressRequest();
        }}
        onCancel={() => setReturnToIntakeInProgressModal(false)}
        confirmButton={'Yes'}
      >
        <div>
          This file request was sent back because it required additional information. Are you sure you want to send the
          request back to Intake-In-Progress?
        </div>
      </ConfirmModal>
    </>
  );
};
